import { HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions, ILogger, LogLevel } from "@microsoft/signalr";

import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Guid } from "@/Common/Models/Guid";
import { IRealTimeNotificationMessage } from "./Dto/RealTimeNotificationMessage";
import { RealTimeNotificationOperation } from "./Dto/RealTimeNotificationOperation";
import { RealTimeNotificationResourceType } from "./Dto/RealTimeNotificationResourceType";
import { RealTimeNotificationState, RealTimeNotificationStateFromSignalRState } from "./RealTimeNotificationState";
import { SignalRCustomRetryPolicy } from "./SignalRCustomRetryPolicy";
import { SignalRLogger } from "./SignalRLogger";

const isDev = process.env.NODE_ENV === "development";

type RealTimeNotificationEventCallback = (client: SignalRClient) => void;

enum RealTimeNotificationEventType {
    StateChanged
}

interface EventRegistration {
    id: string;
    type: RealTimeNotificationEventType;
    callback: RealTimeNotificationEventCallback
}

/** SignalR callback on message reception. */
type messageReceivedCallback = (message: IRealTimeNotificationMessage) => void;

/** SignalR subscription parameters. */
interface Subscription {
    /** SignalR subscription's identifier. */
    id: string;

    /** SignalR type operation code request. */
    type: RealTimeNotificationOperation;

    /** Subscription's method */
    callback: messageReceivedCallback ;
}

/** SignalR subscription channel. */
interface Channel {
    /** Provider ID */
    providerId: Guid;

    /** SignalR ResourceType */
    resourceType: RealTimeNotificationResourceType;
}

/** Client to connect to SignalR service. */
class SignalRClient {
    private readonly logger: ILogger;
    private connection: HubConnection;
    private channels: Channel[] = [];
    private subscriptions: Subscription[] = [];
    private eventRegistrations: EventRegistration[] = [];
    private connectionRetryCount: number = 0;

    constructor() {
        this.logger = new SignalRLogger(isDev ? LogLevel.Information : LogLevel.Warning);
    }

    /** Establish a connection to SignalR. */
    public async connect(): Promise<void> {
        if (!this.isFeatureFlagEnabled()) {
            return;
        }

        if (!apiSettings.ApiKey || !apiSettings.SessionId) {
            throw new Error("Api key or session id is undefined.");
        }

        const options: IHttpConnectionOptions = {
            headers: { "api-key": apiSettings.ApiKey, "sessionId": apiSettings.SessionId },
            logger: this.logger
        };
        const connectionHub = apiSettings.buildAppUrl("hubs/event");

        this.connection = new HubConnectionBuilder()
            .withUrl(connectionHub, options)
            .withAutomaticReconnect(new SignalRCustomRetryPolicy())
            .build();

        await this.startSignalRConnection();
    }

    /** Stops the connection to SignalR. */
    public async disconnect(): Promise<void> {
        if (!this.isFeatureFlagEnabled()) {
            return;
        }

        if (!apiSettings.ApiKey || !apiSettings.SessionId) {
            throw new Error("Api key or session id is undefined.");
        }

        if (!this.connection) {
            throw new Error("The are currently no connection to SignalR.");
        }

        await this.stopSignalRConnection();
    }

    /**
    * Subscribes to the SignalR Hub.
    * @param messageType The operation to subscribe to.
    * @param callback The method to be executed each time the operation is called.
    */
    public subscribe(messageType: RealTimeNotificationOperation, callback: messageReceivedCallback): string {
        const subscription: Subscription = {
            id: GuidHelper.NewGuid(),
            type: messageType,
            callback: callback
        };
        this.subscriptions.push(subscription);
        this.logger.log(LogLevel.Information, `Added subscription "${subscription.id}" to "${EnumHelper.getName(RealTimeNotificationOperation, messageType)}"`);

        return subscription.id;
    }

    /**
    * Unsubscribe from the SignalR Hub.
    * @param id The subscription identifier.
    */
    public unsubscribe(id: string): void {
        const subscription = this.subscriptions.find(s => s.id === id);
        this.subscriptions = this.subscriptions.filter(s => s.id !== id);
        this.logger.log(LogLevel.Information, `Removed subscription "${subscription.id}" to "${EnumHelper.getName(RealTimeNotificationOperation, subscription.type)}"`);
    }

    public registerToEvent(eventType: RealTimeNotificationEventType, callback: RealTimeNotificationEventCallback): string {
        const eventRegistration: EventRegistration = {
            id: GuidHelper.NewGuid(),
            type: eventType,
            callback: callback
        };
        this.eventRegistrations.push(eventRegistration);
        this.logger.log(LogLevel.Information, `Added event registration "${eventRegistration.id}" to "${EnumHelper.getName(RealTimeNotificationEventType, eventType)}"`);

        return eventRegistration.id;
    }

    public unregisterFromEvent(id: string): void {
        const eventRegistration = this.eventRegistrations.find(s => s.id === id);
        this.eventRegistrations = this.eventRegistrations.filter(s => s.id !== id);
        this.logger.log(LogLevel.Information, `Removed event registration "${eventRegistration.id}" to "${EnumHelper.getName(RealTimeNotificationEventType, eventRegistration.type)}"`);
    }

    /**
    * Update the SignalR groups to subscribe to and/or unsubscribe from.
    * @param resourceType The SignalR resource type.
    * @param providers The list of providers.
    */
    public async updateChannelsByProviders(resourceType: RealTimeNotificationResourceType, providers: string[]): Promise<void> {
        const updatedChannels: Channel[] = providers.map<Channel>(
            provider => ({
                providerId: provider,
                resourceType: resourceType
            } as Channel)
        );

        // filter the channels that were not yet added and subscribe to them
        const newChannels = this.filterNewChannels(updatedChannels);
        await this.joinChannels(newChannels);

        if (cacheStore.ClinicSettings.IsCallCenterEnabled()) {
            // filter the channels that are not in the list of channels to update for the specified resource type, remove and unsuscribe from them
            const obsoleteChannels = this.channels.filter(
                channel =>
                    channel.resourceType === resourceType &&
                    !updatedChannels.any(
                        previousChannel =>
                            previousChannel.providerId === channel.providerId
                    )
            );
            await this.quitChannels(obsoleteChannels);
        }
        else {
        // filter the channels that are not in the list of channels to update, remove and unsuscribe from them
            const obsoleteChannels = this.channels
                .filter(channel => !updatedChannels.any(previousChannel => previousChannel.providerId === channel.providerId && previousChannel.resourceType === channel.resourceType));
            await this.quitChannels(obsoleteChannels);
        }
    }

    public get state(): RealTimeNotificationState {
        return RealTimeNotificationStateFromSignalRState(this.connection?.state ?? HubConnectionState.Disconnected);
    }

    private async startSignalRConnection(): Promise<void> {
        try {
            // register events before connecting
            this.registerToMessages();
            this.connection.onreconnected(() => this.channels && this.joinChannels(this.channels));
            this.registerStateChangeEvents();

            await this.runStateChangeAction(() => this.connection.start());
            console.assert(this.connection.state === HubConnectionState.Connected);

            this.logger.log(LogLevel.Information, "Connected");

            if (this.channels)
                await this.joinChannels(this.channels);

            this.connectionRetryCount = 0; // reset the connection retry if the connection is successfull
        }
        catch (err) {
            console.assert(this.connection.state === HubConnectionState.Disconnected);
            this.logger.log(LogLevel.Error, `Connection Error: ${err}`);

            setTimeout(
                () => this.startSignalRConnection(),
                this.getTimeoutValueForConnectionRetry()
            );
            this.connectionRetryCount += 1;
        }
    }

    private async stopSignalRConnection(): Promise<void> {
        try {
            await this.runStateChangeAction(() => this.connection.stop());

            this.logger.log(LogLevel.Information, "Disconnected");

            await this.quitChannels(this.channels);
            this.channels = []; // reset the channels after the connection to signalR is stopped
        }
        catch (err) {
            this.logger.log(LogLevel.Error, `Disconnection Error: ${err}`);
        }
    }

    private registerToMessages(): void {
        this.connection.on("ReceiveMessage", (message: IRealTimeNotificationMessage) => {
            if (IsNullOrUndefined(message.Type)) {
                this.logger.log(LogLevel.Warning, "A message was received without type and cannot be processed.");
                return;
            }

            this.logger.log(LogLevel.Information, `A message for operation "${EnumHelper.getName(RealTimeNotificationOperation, message.Type)}" was received.`);
            this.subscriptions.filter(x => x.type === message.Type).forEach(x => x.callback(message));
        });
    }

    private async joinChannels(channelsToJoin: Channel[]): Promise<void> {
        const orderedChannels = channelsToJoin.groupBy(channel => channel.resourceType);
        for (const channels of orderedChannels) {
            const resourceType: RealTimeNotificationResourceType = channels.firstOrDefault().resourceType;
            const providers: Guid[] = channels.map(channel => channel.providerId);

            try {
                const newlyJoinedChannels = this.filterNewChannels(channelsToJoin);
                this.channels.push(...newlyJoinedChannels);
                await this.connection.invoke("JoinChannelByProviders", resourceType, providers);
                this.logger.log(LogLevel.Information, `Join channel for resource type "${EnumHelper.getName(RealTimeNotificationResourceType, resourceType)}" and providers "${providers.join(",")}"`);
            }
            catch (error) {
                this.logger.log(LogLevel.Error, `${error.toString()}`);
            }
        }
    }

    private async quitChannels(obsoleteChannels: Channel[]): Promise<void> {
        const orderedChannel = obsoleteChannels.groupBy(channel => channel.resourceType);
        for (const channels of orderedChannel) {
            const resourceType: RealTimeNotificationResourceType = channels.firstOrDefault().resourceType;
            const providers: Guid[] = channels.map(group => group.providerId);

            try {
                this.channels = this.channels.filter(channel => !obsoleteChannels.contains(channel));
                await this.connection.invoke("QuitChannelByProviders", resourceType, providers);
                this.logger.log(LogLevel.Information, `Quit the channel for resource type "${EnumHelper.getName(RealTimeNotificationResourceType, resourceType)}" and providers "${providers.join(",")}"`);
            }
            catch (error) {
                this.logger.log(LogLevel.Error, `${error.toString()}`);
            }
        }
    }

    private isFeatureFlagEnabled(): boolean {
        return cacheStore?.ClinicSettings?.EnableRealTimeNotifications;
    }

    private getTimeoutValueForConnectionRetry(): number {
        return this.connectionRetryCount < 10 ? 1000 : 10000;
    }

    private filterNewChannels(channels: Channel[]): Channel[] {
        return channels.filter(channel =>
            !this.channels.any(previousChannel => previousChannel.providerId === channel.providerId && previousChannel.resourceType === channel.resourceType)
        );
    }

    private async runStateChangeAction(func: () => Promise<void>): Promise<void> {
        const result = func();
        this.onStateChanged();
        try {
            await result;
        }
        finally {
            this.onStateChanged();
        }
    }

    private registerStateChangeEvents(): void {
        const boundStateChange = this.onStateChanged.bind(this);
        this.connection.onclose(boundStateChange);
        this.connection.onreconnecting(boundStateChange);
        this.connection.onreconnected(boundStateChange);
    }

    private onStateChanged(): void {
        this.eventRegistrations
            .filter(eventRegistration => eventRegistration.type === RealTimeNotificationEventType.StateChanged)
            .forEach(eventRegistration => {
                eventRegistration.callback(this);
            });
    }
}

const singleton = new SignalRClient();

export { messageReceivedCallback, singleton as realTimeNotificationClient, RealTimeNotificationEventCallback, RealTimeNotificationEventType };
